// pages/sections/QuotedFeeSection.js
// Client Quoted Fee Review — Partner-only. Lists the prior-year jobs of the partner's clients
// (admins see firm-wide) with WIP / billing context and a suggested quote. "Quote Fee" opens a
// modal that defaults to the greater of 107% of PY and Office Realization (rounded up to $100); the
// partner submits or edits it. Submitting writes the Tax / Audit & Assurance fee to the client's
// Knuula record and only then marks the row submitted — there is no separate sign-off.
// An 'Expected Tech Fee' (Steering's global Tech Fee Calc, set from the header button by STEERING_USERS / admins)
// is added on top of the partner's quote: the suggested figure is a total, the modal opens on suggested - tech fee,
// and the total (typed + tech fee) is what goes to Knuula.
// Submitting is OPTIMISTIC: the row reads done immediately and the modal closes, while the (slow,
// synchronous) Knuula write runs in the background through useRowMutations. The server records the
// submission only if Knuula confirms the figure; if it answers anything else (dry run, partial / unconfirmed,
// refusal, timeout, error) the row is put back and the server's message is shown in a toast.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { usePeriod } from '../../hooks/usePeriod';
import { useSectionData } from '../../hooks/useSectionData';
import { useRowMutations } from '../../hooks/useRowMutations';
import LastRefreshed from '../../components/LastRefreshed';
import QuotedFeeModal from './QuotedFeeModal';
import TechFeeCalcModal from './TechFeeCalcModal';
import PeJobLink from './PeJobLink';
import ReviewPagination, { REVIEW_PAGE_SIZE as PAGE_SIZE } from './ReviewPagination';
import { formatMoney } from './clientDataUtil';
import { doneTip, fmtHours, postJson, postAdminUnmark, sortDoneLast } from './reviewUtil';
import './ReviewTable.css';
import './QuotedFeeSection.css';

const SECTION_KEY = 'client-quoted-fee';
const FEE_LABEL = { tax: 'Tax', aa: 'A&A' };

const money = n => (n == null ? '—' : formatMoney(n, { cents: true }));
// Time is hours to the nearest hundredth.
const hours = n => (n == null || !Number.isFinite(Number(n)) ? '—' : fmtHours(n));

export default function QuotedFeeSection() {
  const { email, isAdmin, isSteering } = useAuth();
  const { period, periodActive } = usePeriod();

  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'pending' | 'submitted'
  const [typeFilter, setTypeFilter]   = useState('');   // '' | 'tax' | 'aa'
  const [officeFilter, setOfficeFilter] = useState('');
  const [page, setPage]               = useState(1);
  const [quoting, setQuoting]         = useState(null); // the row whose modal is open
  const [unmarking, setUnmarking]     = useState(new Set());
  const [techCalcOpen, setTechCalcOpen] = useState(false);

  const fetcher = useCallback(async () => {
    if (!period) return null;
    const res = await fetch(`/api/diQuotedFee/${period}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [period]);

  // Optimistic saves: ordered per job, and a heartbeat refetch can't overwrite a quote still being written.
  const mutations = useRowMutations(r => r.jobIdx);
  const { pinned, clearPinned } = mutations;

  const { data, setData, initialLoading, error, lastRefreshedAt, freshness, silentRefetch }
    = useSectionData(fetcher, {
      deps: [period, email],
      heartbeat: { sections: [SECTION_KEY], period },
      reconcile: mutations.reconcile,
    });

  // Rows quoted this session stay listed (e.g. under "Needs Quote") until the user changes a filter.
  useEffect(() => { clearPinned(); }, [search, statusFilter, typeFilter, officeFilter, clearPinned]);

  const setRowQuote = (jobIdx, quote) => setData(prev => (prev ? {
    ...prev,
    rows: (prev.rows || []).map(r => (String(r.jobIdx) !== String(jobIdx) ? r : { ...r, quote })),
  } : prev));

  // Optimistic: the row reads done and the modal closes at once. `row` is the RENDERED row, so its
  // `quote` is the revert snapshot. The server records the submission only if Knuula confirms the
  // figure; any other answer (dry run, partial / unconfirmed, refusal, timeout, error) reverts the row
  // and shows the server's message.
  function submitQuote(row, amount, techFee) {
    const before = row.quote || { submitted: false };
    setRowQuote(row.jobIdx, {
      submitted: true, submittedAt: new Date().toISOString(), submittedBy: email, amount, feeType: row.feeType,
    });
    setQuoting(null);
    const label = `${row.clientCode}: ${FEE_LABEL[row.feeType] || 'fee'} quote of ${money(amount)}`;
    mutations.run(
      row.jobIdx,
      () => postJson(`/api/diQuotedFee/${period}`, { jobIdx: row.jobIdx, amount, techFee }),
      { revert: () => setRowQuote(row.jobIdx, before), refetch: silentRefetch },
    ).then(
      () => { toast.success(`${label} written to Knuula.`); silentRefetch(); },
      err => toast.error(`${label} was NOT saved: ${err.message}`, { autoClose: 15000 }),
    );
  }

  async function adminUnmark(row) {
    if (!isAdmin) return;
    if (!window.confirm(`Unmark ${row.clientCode} — ${row.jobName} as submitted? The fee already in Knuula is not changed.`)) return;
    setUnmarking(prev => new Set(prev).add(row.jobIdx));
    try {
      await postAdminUnmark(period, SECTION_KEY, row.jobIdx, 'partner');
      toast.success('Unmarked.');
      silentRefetch();
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setUnmarking(prev => { const n = new Set(prev); n.delete(row.jobIdx); return n; });
    }
  }

  const rows = useMemo(() => data?.rows || [], [data]);
  const offices = useMemo(() => [...new Set(rows.map(r => r.clientOffice))].filter(Boolean).sort(), [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(r =>
        (r.clientCode || '').toLowerCase().includes(s) ||
        (r.clientName || '').toLowerCase().includes(s) ||
        (r.jobName || '').toLowerCase().includes(s));
    }
    if (typeFilter) out = out.filter(r => r.feeType === typeFilter);
    if (isAdmin && officeFilter) out = out.filter(r => r.clientOffice === officeFilter);
    // Rows the user just quoted stay put even if that moved them out of this status.
    const keep = r => pinned.has(String(r.jobIdx));
    if (statusFilter === 'pending')   out = out.filter(r => keep(r) || !r.quote?.submitted);
    if (statusFilter === 'submitted') out = out.filter(r => keep(r) ||  r.quote?.submitted);
    return sortDoneLast(out, r => !!r.quote?.submitted);
  }, [rows, search, typeFilter, officeFilter, statusFilter, isAdmin, pinned]);

  // From rows (not data.summary) so a just-submitted quote moves the bar.
  const summary = useMemo(() => {
    const total = rows.length;
    const submitted = rows.filter(r => r.quote?.submitted).length;
    return { total, submitted, pending: total - submitted };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (initialLoading) return <div className="rt-loading">Loading quoted fee review…</div>;
  if (error && !data) return <div className="rt-error">Error: {error}</div>;
  if (!data) return null;

  const readOnly = !periodActive;
  // Why a row's quote button is disabled, or '' when it can be used. A&A is held back until the
  // Knuula A&A write is proven (api/shared/knuulaFee.js aaFeeEnabled).
  const blockedReason = row => {
    if (!row.feeType) return "This job's type has no Knuula fee field — ask an admin.";
    if (row.feeType === 'aa' && !data.aaEnabled) return 'Audit & Assurance quotes are not enabled yet.';
    return '';
  };
  const pct = summary.total > 0 ? Math.round((summary.submitted / summary.total) * 100) : 0;

  return (
    <div className="rt-page">
      <div className="rt-header">
        <div className="rt-title-block">
          <h2 className="rt-title">Client Quoted Fee Review</h2>
          {readOnly && <span className="rt-readonly-badge">Read-only</span>}
          <LastRefreshed at={lastRefreshedAt} freshness={freshness} onRefresh={silentRefetch} />
        </div>
        {isSteering && (
          <button type="button" className="rt-btn" onClick={() => setTechCalcOpen(true)}>
            <i className="fas fa-calculator" /> Tech Fee Calc
          </button>
        )}
      </div>
      <p className="rt-subtitle">
        Your clients&apos; prior-year jobs with their billing and WIP. Use <b>Quote Fee</b> to set the fee we will quote for the
        job: it starts at the greater of 107% of last year and the office-realization figure (rounded up to the next
        $100), less the expected tech fee, which is added back on top; you can change it before submitting. Submitting
        writes the total to Knuula and completes the job.
      </p>

      <div className="rt-progress-section">
        <div className="rt-progress-header">
          <h3 className="rt-progress-title">{isAdmin ? 'Firm Progress' : 'Your Progress'}</h3>
          <span className="rt-progress-label-val">
            <span className="rt-progress-count">{summary.submitted} / {summary.total}</span>
            <span className="rt-progress-divider">—</span>
            {pct}% Complete
          </span>
        </div>
        <div className="rt-progress-track">
          <div
            className="rt-progress-fill"
            style={{
              width: `${Math.min(100, Math.max(0, pct))}%`,
              background: pct >= 100 ? '#1a7a4a' : pct >= 50 ? '#e6a817' : '#c0392b',
            }}
          />
        </div>
      </div>

      <div className="rt-summary">
        <div className="rt-stat"><span className="rt-stat-val">{summary.total}</span><span className="rt-stat-lbl">{isAdmin ? 'Jobs' : 'Your Jobs'}</span></div>
        <div className="rt-stat rt-stat--pending"><span className="rt-stat-val">{summary.pending}</span><span className="rt-stat-lbl">Needs Quote</span></div>
        <div className="rt-stat rt-stat--done"><span className="rt-stat-val">{summary.submitted}</span><span className="rt-stat-lbl">Done</span></div>
      </div>

      <div className="rt-filters">
        <input className="rt-filter-input" placeholder="Search by client code, client name, or job…"
               value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select className="rt-filter-select" value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          <option value="tax">Tax</option>
          <option value="aa">Audit &amp; Assurance</option>
        </select>
        <select className="rt-filter-select" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="pending">Needs Quote</option>
          <option value="submitted">Done</option>
        </select>
        {isAdmin && (
          <select className="rt-filter-select" value={officeFilter}
                  onChange={e => { setOfficeFilter(e.target.value); setPage(1); }}>
            <option value="">All Offices</option>
            {offices.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
      </div>

      <div className="rt-table-wrap">
        <table className="rt-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>PY Job</th>
              <th>Type</th>
              <th className="qf-num">Hours</th>
              <th className="qf-num" title="WIP time (expenses underneath)">WIP</th>
              <th className="qf-num">Billed</th>
              <th className="qf-num" title="Billed ÷ WIP time">Realization</th>
              <th className="qf-num" title="107% of the prior-year billing">107% PY</th>
              <th className="qf-num" title="WIP time at the office standard realization">Office Realization</th>
              <th className="qf-num" title="The greater of 107% PY and Office Realization, rounded up to the next $100. The total that goes to Knuula, expected tech fee included.">Suggested</th>
              <th className="qf-num" title="Added on top of the fee you enter: Steering's minimum, or the % of WIP time above it, in $50 steps">Expected Tech Fee</th>
              {isAdmin && <th>Partner</th>}
              <th className="qf-action-cell">Quote Fee</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(row => {
              const q = row.quote || {};
              const submitted = !!q.submitted;
              const submittedTip = submitted ? doneTip({ by: q.submittedBy, at: q.submittedAt }, 'Submitted') : undefined;
              return (
                <tr key={row.jobIdx} className={submitted ? 'rt-row-reviewed' : ''}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{row.clientCode}</div>
                    <div className="qf-sub">{row.clientName}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{row.jobName}</div>
                    <PeJobLink jobIdx={row.jobIdx} />
                  </td>
                  <td>
                    {row.feeType
                      ? <span className={`rt-chip qf-chip--${row.feeType}`}>{FEE_LABEL[row.feeType]}</span>
                      : <span className="rt-chip qf-chip--bad" title="This job's type has no Knuula fee field.">{row.jobType || 'Unknown'}</span>}
                  </td>
                  <td className="qf-num">{hours(row.hours)}</td>
                  <td className="qf-num">
                    <div>{money(row.wipTime)}</div>
                    <div className="qf-sub">exp {money(row.wipExp)}</div>
                  </td>
                  <td className="qf-num">{money(row.billed)}</td>
                  <td className="qf-num">{row.realization ?? '—'}</td>
                  <td className="qf-num">{money(row.billAt107)}</td>
                  <td className="qf-num">{money(row.officeStandard)}</td>
                  <td className="qf-num qf-suggested">{money(row.suggested)}</td>
                  <td className="qf-num qf-tech">{row.techFee > 0 ? money(row.techFee) : '—'}</td>
                  {isAdmin && <td>{row.clientPartner || <span className="rt-cell-muted">—</span>}</td>}
                  <td className="qf-action-cell">
                    {submitted ? (
                      <span className="qf-done">
                        <span className="rt-done qf-done-amt" title={submittedTip}><i className="fas fa-check-circle" />{money(q.amount)}</span>
                        <span>
                          <button type="button" className="rt-link" disabled={readOnly || !!blockedReason(row)}
                                  title={blockedReason(row) || undefined}
                                  onClick={() => setQuoting(row)}>Edit quote</button>
                          {isAdmin && (
                            <>
                              {' · '}
                              <button type="button" className="rt-link" disabled={readOnly || unmarking.has(row.jobIdx)}
                                      onClick={() => adminUnmark(row)}>Unmark</button>
                            </>
                          )}
                        </span>
                      </span>
                    ) : (
                      <button type="button" className="rt-btn rt-btn--primary"
                              disabled={readOnly || !!blockedReason(row)}
                              title={blockedReason(row) || undefined}
                              onClick={() => setQuoting(row)}>
                        Quote Fee
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {summary.total === 0 && (
          <div className="rt-empty">No prior-year jobs to quote.</div>
        )}
        {filtered.length === 0 && summary.total > 0 && (
          <div className="rt-empty">No jobs match.</div>
        )}
      </div>

      <ReviewPagination page={safePage} totalPages={totalPages} rowCount={filtered.length} onPage={setPage} />

      {quoting && (
        <QuotedFeeModal
          row={quoting}
          onClose={() => setQuoting(null)}
          onSubmit={(total, techFee) => { submitQuote(quoting, total, techFee); }}
        />
      )}

      {techCalcOpen && isSteering && (
        <TechFeeCalcModal
          period={period}
          settings={data.techFeeSettings}
          onClose={() => setTechCalcOpen(false)}
          onSaved={async () => {
            await silentRefetch();
            toast.success('Tech fee settings saved.');
            setTechCalcOpen(false);
          }}
        />
      )}
    </div>
  );
}
